import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { MARKETPLACE_CONFIG } from '@com/core/common/utils/config';
import { MapContext } from '@com/core/common/contexts/mapContext';
import { useTranslation } from '@com/core/common/translations/i18n';
import { baseConfig } from '@com/core/common/baseConfig';
import { TMarker } from '@com/core/common/types/map/googlemap';
import BaseMap from './BaseMap';
import SearchBox from './SearchBox';

type TProps = {
    title?: string;
    customMarkers?: TMarker[];
    customCenter?: { lat: number; lng: number };
    customContainerStyle?: { [key: string]: string };
};

const { googleMapAPIKey } = baseConfig.map;

const Map = ({ customCenter, customContainerStyle, customMarkers, title }: TProps): JSX.Element => {
    const { t } = useTranslation('common');

    const { error, getLocation, locateMeEnabled, mapRef, searchEnabled, updateError } = useContext(MapContext);

    return (
        <MapPrimaryContainer style={customContainerStyle}>
            <div className="mapSection">
                {searchEnabled && <SearchBox placeholder={t('search-for-your-location')} />}
                {locateMeEnabled && navigator && navigator.geolocation && (
                    <button
                        type="button"
                        className="locateBtn"
                        onClick={getLocation}
                        aria-label={t('accessible.locate')}>
                        <img src="/simpleIcons/crosshair.svg" alt="locate" />
                        Locate Me
                    </button>
                )}
                <BaseMap
                    containerElement={<MapContainer style={customContainerStyle} />}
                    mapElement={<div style={{ height: '100%' }} />}
                    loadingElement={<div style={{ flex: 1 }} />}
                    customMarkers={customMarkers}
                    customCenter={customCenter}
                    mapRef={mapRef}
                    googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${googleMapAPIKey}&v=3.exp`}
                    collectionPointsEnabled={false}
                />
            </div>
            {error && (
                <ErrorContainer>
                    <ErrorInnerContainer>
                        <span>
                            <img src={MARKETPLACE_CONFIG.assets.icons.sadFace} alt="mapError" />
                            {error}
                        </span>
                        <ErrorButton
                            onClick={(): void => {
                                updateError(null);
                            }}
                            aria-label={t('accessible.close')}>
                            <img src={MARKETPLACE_CONFIG.assets.icons.x} alt="mapError" />
                        </ErrorButton>
                    </ErrorInnerContainer>
                </ErrorContainer>
            )}
        </MapPrimaryContainer>
    );
};

const MapPrimaryContainer = styled.div`
    .mapSection {
        position: relative;
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        overflow: hidden;
    }

    .locateBtn {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        position: absolute;
        top: 82px;
        ${({ theme: { right } }): string => `${right}: 16px;`}
        z-index: 1;
        display: flex;
        align-items: center;
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        font-size: 0.875rem;
        font-weight: 600;
        height: 36px;
        border-radius: 18px;
        padding: 0 16px;

        img {
            width: 18px;
            ${({ theme: { right } }): string => `margin-${right}: 8px;`}
        }

        &:hover {
            opacity: 0.8;
        }
    }
`;

const MapContainer = styled.div`
    height: 500px;
    display: flex; /* for the loader */
    flex-direction: column;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
`;

const ErrorContainer = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 11px;
    vertical-align: middle;
    position: relative;
    top: -177px;
    ${({ theme: { isRTL } }): string => (isRTL ? `left: unset; right:0.5%;` : `left 0.5%; right:unset;`)}
    border-radius: 3px;
    background-color: rgba(253, 236, 236, 0.97); /* TODO */
    border: solid 1px #fbd3d8; /* TODO */
    color: #cb7580; /* TODO */
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.03); /* TODO */
    width: 99%;
`;

const ErrorInnerContainer = styled.div`
    vertical-align: middle;
    margin-right: 10px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    span {
        display: flex;
        img {
            ${({ theme: { isRTL } }): string => (isRTL ? `padding: 0 0 0 5px;` : `padding: 0 5px 0 0;`)}
        }
    }
`;

const ErrorButton = styled.button``;

export default Map;
